import { computed, defineComponent, h, type PropType, type VNode } from "vue";
import { useI18n } from "vue-i18n";
import ChatContestLinkButton from "./chat-contest-link-button.vue";
import {
  parseChatMessageContent,
  type ChatMessageContentInlineNode,
  type ChatMessageContentRootNode,
} from "./chat-message-content-nodes";
import { useChatContestLinkConfig } from "./use-chat-contest-link-config";

type RenderContext = {
  interactive: boolean;
  featureFlagsLoaded: boolean;
  getContestLinkHref: (href: string) => string | null;
  t: (key: string) => string;
};

export default defineComponent({
  name: "ChatMessageText",
  props: {
    text: {
      type: String,
      required: true,
    },
    interactive: {
      type: Boolean as PropType<boolean>,
      default: true,
    },
  },
  setup(props) {
    const { t } = useI18n();
    const { loaded, getContestLinkHref } = useChatContestLinkConfig();
    const contentTree = computed(() => parseChatMessageContent(props.text));

    return () =>
      renderRootNode(contentTree.value, {
        interactive: props.interactive,
        featureFlagsLoaded: loaded.value,
        getContestLinkHref,
        t,
      });
  },
});

function renderRootNode(
  node: ChatMessageContentRootNode,
  context: RenderContext,
): VNode {
  return h(
    "span",
    node.children.map((child) => renderInlineNode(child, context)),
  );
}

function renderInlineNode(
  node: ChatMessageContentInlineNode,
  context: RenderContext,
): string | VNode {
  if (node.type === "text") return node.value;

  if (!context.featureFlagsLoaded) {
    return h(
      "span",
      { class: "text-text-brand underline underline-offset-2" },
      context.t("channel.chat.link"),
    );
  }

  const contestHref = context.getContestLinkHref(node.href);
  if (contestHref) {
    return renderContestNode(contestHref, context);
  }

  if (!context.interactive) return node.value;

  return h(
    "a",
    {
      href: node.href,
      target: "_blank",
      rel: "noopener noreferrer",
      class: "text-text-brand underline underline-offset-2",
      onClick: stopPropagation,
      onKeydown: stopActivationKeyPropagation,
      onKeyup: stopActivationKeyPropagation,
    },
    node.value,
  );
}

function renderContestNode(href: string, context: RenderContext): VNode {
  if (context.interactive) {
    return h(ChatContestLinkButton, { href });
  }

  return h(
    "span",
    { class: "text-text-brand font-semibold underline underline-offset-2" },
    context.t("channel.chat.contestLink.enterContest"),
  );
}

function stopPropagation(event: Event) {
  event.stopPropagation();
}

function stopActivationKeyPropagation(event: KeyboardEvent) {
  if (event.key === "Enter" || event.key === " " || event.code === "Space") {
    event.stopPropagation();
  }
}
